import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@apollo/client';
import { ICellEditorParams } from 'ag-grid-community';

import { CustomAutocomplete } from '@/components/common/custom-autocomplete';
import useDebounce from '@/components/hooks/useDebounce';
import {
  SEARCH_SEMEN_BY_ACCOUNT,
  SearchSemenByAccountFragment,
} from '@/types.gql';

import { BulkDonorScheduleState } from '../../hooks/use-bulk-donor-schedule-store';

export interface SemenAutocompleteCellEditorProps extends ICellEditorParams {
  context: BulkDonorScheduleState;
  fertilizationSiteId: string | undefined;
}

export const SemenAutocompleteCellEditor = forwardRef(
  (props: SemenAutocompleteCellEditorProps, ref) => {
    const { value, data, node, fertilizationSiteId } = props;
    const [inputValue, setInputValue] = useState<string>(value || '');
    const inputRef = useRef<HTMLInputElement>(null);
    const accountCode = data?.accountCode;

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearch = useDebounce(searchQuery);
    const { data: searchData } = useQuery(SEARCH_SEMEN_BY_ACCOUNT, {
      variables: {
        fertilizationSiteId: fertilizationSiteId!,
        accountCode: accountCode!,
        search: debouncedSearch,
        selectionLimit: 100,
      },
      skip: !accountCode || !fertilizationSiteId || debouncedSearch.length < 1,
    });
    const searchItems = searchData?.semen?.items;
    const semenDataMap = useMemo(
      () =>
        new Map<string, SearchSemenByAccountFragment>(
          searchItems
            ?.filter((s) => s.sire?.name)
            .map(
              (semen) =>
                [semen.id, semen] as [string, SearchSemenByAccountFragment],
            ) ?? [],
        ),
      [searchItems],
    );

    const semenIds = useMemo(
      () => Array.from(semenDataMap.keys()),
      [semenDataMap],
    );

    useImperativeHandle(ref, () => ({
      getValue: () => inputValue,
      isCancelAfterEnd: () => false,
      isPopup: () => true,
    }));

    const handleSearch = useCallback((query: string) => {
      setSearchQuery(query);
    }, []);

    const handleSelect = useCallback(
      (id: string) => {
        const semen = semenDataMap.get(id);
        if (!semen?.sire?.name) {
          return;
        }
        const selectedSireName = semen.sire.name;
        setInputValue(selectedSireName);

        const rowData = node?.data ?? data ?? {};
        const updatedData = {
          ...rowData,
          semen: selectedSireName,
          sire: selectedSireName,
          sireBreed: semen.sire.breed?.code || rowData.sireBreed,
          sireRegistrationNumber:
            semen.sire.registrationNumber || rowData.sireRegistrationNumber,
          collectionCode: semen.colCode || rowData.collectionCode,
          naab: semen.naab || rowData.naab,
          cane: semen.cane || semen.sire.cane || rowData.cane,
          semenType: semen.semenType || rowData.semenType,
        };
        node.updateData(updatedData);
      },
      [semenDataMap, node, data],
    );

    return (
      <CustomAutocomplete
        ref={inputRef}
        value={inputValue}
        suggestions={semenIds}
        onSearch={handleSearch}
        onChange={setInputValue}
        onSelect={handleSelect}
        itemTemplate={(id) => {
          const semen = semenDataMap.get(id);
          if (!semen || !semen.sire) {
            return null;
          }

          return (
            <>
              {semen.sire!.name}
              {semen.sire?.breed?.code ? <b> {semen.sire.breed.code}</b> : null}
              {semen.colCode ? ` (${semen.colCode})` : null}
              {semen.naab ? ` NAAB: ${semen.naab}` : null}
              {semen.sire?.registrationNumber
                ? ` #${semen.sire.registrationNumber}`
                : null}
            </>
          );
        }}
        showDropdownButton
        autoFocus
        minLength={1}
        className="w-full"
        inputClassName="w-full p-1 text-sm"
        panelClassName="text-sm"
        disabled={!accountCode}
        placeholder={
          !fertilizationSiteId
            ? 'Select Fertilization Site'
            : !accountCode
              ? 'Enter Account Code'
              : 'Type to search...'
        }
      />
    );
  },
);

SemenAutocompleteCellEditor.displayName = 'SemenAutocompleteCellEditor';
